import React from 'react';

/**
 * Phone-facing question card. Renders a question text and 3 option buttons.
 * Once the participant taps an option, the choice is locked and the card
 * shows the locked-in answer while waiting for the animator to advance.
 *
 * Props:
 *   - question: { text, options: [{ text, axis }] }
 *   - questionNumber: int (1-indexed for display)
 *   - totalQuestions: int
 *   - selectedOptionIndex: number | null (null until they answer)
 *   - locked: bool — true once any answer has been submitted for this Q
 *   - submitting: bool — show in-flight feedback on the chosen option
 *   - onSelect(optionIndex)
 */
export default function ParticipantQuestionCard({
  question,
  questionNumber,
  totalQuestions,
  selectedOptionIndex,
  locked,
  submitting,
  onSelect
}) {
  if (!question) return null;

  return (
    <div
      style={{
        background: 'white',
        borderRadius: 12,
        padding: '1.5rem',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem'
      }}
    >
      <div style={{ fontSize: '0.85rem', color: '#7f8c8d', fontWeight: 600 }}>
        Question {questionNumber} / {totalQuestions}
      </div>
      <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#2c3e50', lineHeight: 1.3 }}>
        {question.text}
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {question.options.map((opt, idx) => {
          const isSelected = idx === selectedOptionIndex;
          const isDisabled = locked || submitting;
          const baseStyle = {
            padding: '1rem 1.25rem',
            borderRadius: 10,
            border: '2px solid',
            fontSize: '1.05rem',
            lineHeight: 1.35,
            textAlign: 'left',
            cursor: isDisabled ? 'default' : 'pointer',
            transition: 'all 0.15s ease',
            width: '100%'
          };
          let style;
          if (isSelected) {
            style = { ...baseStyle, borderColor: '#27ae60', background: '#e8f8f0', color: '#1e8449', fontWeight: 600 };
          } else if (locked) {
            style = { ...baseStyle, borderColor: '#ecf0f1', background: '#f8f9fa', color: '#bdc3c7' };
          } else {
            style = { ...baseStyle, borderColor: '#d6dde3', background: 'white', color: '#2c3e50' };
          }
          return (
            <button
              key={idx}
              type="button"
              onClick={() => !isDisabled && onSelect(idx)}
              disabled={isDisabled}
              style={style}
            >
              {opt.text}
              {isSelected && <span style={{ marginLeft: '0.5em' }}>✓</span>}
            </button>
          );
        })}
      </div>

      {locked && (
        <div
          style={{
            padding: '0.75rem 1rem',
            background: '#f0f7ff',
            borderRadius: 8,
            color: '#2980b9',
            fontSize: '0.9rem',
            textAlign: 'center'
          }}
        >
          Réponse enregistrée. En attente du prochain mouvement de l'animateur…
        </div>
      )}
    </div>
  );
}
